import { sinVerComo } from "@/lib/impersonation/ver-como-lectura";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

/**
 * Impersonación del Portal Condominio ("Ver como").
 *
 * Solo para roles con `roles.puede_impersonar`. Al impersonar a un usuario de
 * condominio (ej. Supervisor Condominio / Operador Condomino), el portal se ve
 * COMO ese usuario: su ROL define los menús visibles y su EMAIL define los
 * condominios accesibles (proyectos_acceso). Espejo de CobranzaImpersonation.
 *
 * Lo guardado en localStorage no se cree a ciegas:
 *   - Si el usuario REAL no tiene `puede_impersonar`, se descarta y se borra.
 *   - Rol, persona y auth_user_id del impersonado se leen de `usuarios` (activo) y no del
 *     storage; si ya no existe, está inactivo o no tiene cuenta auth, se descarta. Mientras se
 *     resuelve, las pantallas quedan en esqueleto y en solo lectura.
 * Lecturas: las RPC del portal reciben `p_auth_user_id` y el servidor calcula con la identidad
 * del impersonado (lo valida contra `puede_impersonar` del usuario real). Escrituras: ninguna
 * mientras se impersona (`usePortalPagePermissions`), y el servidor siempre firma con la sesión.
 */

interface CondominioImpersonationContextType {
  impersonatedEmail: string | null;
  impersonatedName: string | null;
  impersonatedPersonaId: number | null;
  impersonatedRolId: number | null;
  /** `usuarios.auth_user_id` del impersonado: lo reciben las RPC de lectura ("ver como"). */
  impersonatedAuthUserId: string | null;
  setImpersonated: (
    email: string, name: string, personaId: number | null, rolId: number | null, rolNombre?: string | null,
  ) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const STORAGE_KEY = "sozu-condominio-impersonation";

const CondominioImpersonationContext = createContext<CondominioImpersonationContextType>({
  impersonatedEmail: null,
  impersonatedName: null,
  impersonatedPersonaId: null,
  impersonatedRolId: null,
  impersonatedAuthUserId: null,
  setImpersonated: () => {},
  clearImpersonation: () => {},
  isImpersonating: false,
});

interface Persisted {
  email: string | null;
  name: string | null;
  personaId: number | null;
  /** Rol con el que entra al portal según "Ver como" (puede ser un rol adicional). */
  rolId: number | null;
  rolNombre?: string | null;
}

function readPersisted(): Persisted {
  if (typeof window === "undefined") return { email: null, name: null, personaId: null, rolId: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { email: null, name: null, personaId: null, rolId: null };
    const p = JSON.parse(raw);
    return {
      email: p.email ?? null,
      name: p.name ?? null,
      personaId: p.personaId ?? null,
      rolId: p.rolId ?? null,
      rolNombre: p.rolNombre ?? null,
    };
  } catch {
    return { email: null, name: null, personaId: null, rolId: null };
  }
}

export function CondominioImpersonationProvider({ children }: { children: ReactNode }) {
  const { profile, isProfileLoading } = useAuth();
  const initial = readPersisted();
  const [email, setEmail] = useState<string | null>(initial.email);
  const [name, setName] = useState<string | null>(initial.name);
  const [personaId, setPersonaId] = useState<number | null>(initial.personaId);
  const [rolAcceso, setRolAcceso] = useState<{ id: number | null; nombre: string | null }>({
    id: initial.rolId, nombre: initial.rolNombre ?? null,
  });

  const persist = (p: Persisted) => {
    try {
      if (p.email == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* noop */
    }
  };

  // Perfil ya cargado y sin `puede_impersonar`: lo guardado no vale.
  const perfilListo = !!profile && !isProfileLoading;
  const puedeImpersonar = profile?.puede_impersonar === true;
  const sinDerecho = perfilListo && !puedeImpersonar;

  // Rol y persona del impersonado desde la BD (el storage se puede editar a mano).
  const objetivo = useQuery({
    queryKey: ["condominio-impersonado", email],
    enabled: puedeImpersonar && !!email,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // Con la sesión real: con "ver como" activo, la lectura de otra persona saldría con el
      // acceso de la anterior y el cambio de persona fallaría.
      const { data, error } = await sinVerComo((supabase as any)
        .from("usuarios")
        .select("rol_id, id_persona, auth_user_id")
        .eq("email", email)
        .eq("activo", true))
        .maybeSingle();
      if (error) throw error;
      return (data as { rol_id: number | null; id_persona: number | null; auth_user_id: string | null } | null) ?? null;
    },
  });
  // Sin fila activa, sin rol o sin cuenta auth (o sin poder leerla): no se impersona a medias;
  // sin auth_user_id el servidor no podría calcular su vista y se vería la del admin.
  const objetivoInvalido =
    objetivo.isError ||
    (objetivo.isSuccess && (objetivo.data == null || objetivo.data.rol_id == null || !objetivo.data.auth_user_id));

  const clearImpersonation = () => {
    setEmail(null);
    setName(null);
    setPersonaId(null);
    setRolAcceso({ id: null, nombre: null });
    persist({ email: null, name: null, personaId: null, rolId: null });
  };

  useEffect(() => {
    if (email && (sinDerecho || objetivoInvalido)) clearImpersonation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, sinDerecho, objetivoInvalido]);

  const setImpersonated = (e: string, n: string, pid: number | null, rid: number | null, rn?: string | null) => {
    if (!puedeImpersonar) return;
    setEmail(e);
    setName(n);
    setPersonaId(pid);
    setRolAcceso({ id: rid, nombre: rn ?? null });
    persist({ email: e, name: n, personaId: pid, rolId: rid, rolNombre: rn ?? null });
  };

  const activo = !!email && !sinDerecho && !objetivoInvalido;
  const rolId = activo ? rolAcceso.id ?? objetivo.data?.rol_id ?? null : null;

  // Publica al núcleo global (menús/rol efectivos y solo lectura de toda la app) desde que hay
  // selección: mientras se valida al impersonado ya es solo lectura, sin rol (nunca el del admin).
  usePublishImpersonationTarget(
    email
      ? {
          email, personaId, nombre: name, rolId, rolNombre: rolAcceso.nombre,
          // Con él las lecturas del servidor responden como la persona (header `x-ver-como`).
          authUserId: activo ? objetivo.data?.auth_user_id ?? null : null,
        }
      : null,
  );

  return (
    <CondominioImpersonationContext.Provider
      value={{
        impersonatedEmail: activo ? email : null,
        impersonatedName: activo ? name : null,
        impersonatedPersonaId: activo ? objetivo.data?.id_persona ?? personaId : null,
        impersonatedRolId: rolId,
        impersonatedAuthUserId: activo ? objetivo.data?.auth_user_id ?? null : null,
        setImpersonated,
        clearImpersonation,
        isImpersonating: activo,
      }}
    >
      {children}
    </CondominioImpersonationContext.Provider>
  );
}

export const useCondominioImpersonation = () => useContext(CondominioImpersonationContext);
